import React, {useEffect, useState} from 'react';
import {IonAlert, IonCol, IonLabel, IonLoading, IonRow, IonToast} from '@ionic/react';
import {useTranslation} from 'react-i18next';
import {Controller, FormProvider, useForm} from "react-hook-form";
import {useHistory, useLocation} from "react-router-dom";
import axios from "axios";
import Form from "@components/form";
import Accordion from "@components/accordion/accordion";
import CityAutocomplete from "@components/google/cityAutocomplete/cityAutocomplete.component";
import PaginatedListModal from "@components/modal/paginatedListModal.component";
import {StyledButton, StyledButtonWrapper, StyledTravelAllowanceContent,} from "@app/travelAllowance/travelAllowance.style";
import {StyledInput, StyledInputGroup, StyledInputUnit} from "@components/form/input/input.style";
import {
    addRideRoute,
    editRideRoute,
    getPlacesOfService,
    getPossibleCoordinators,
    validateRideRoute
} from "@services/travelAllowance/rideRoute.service";
import {PlaceOfService, RideRoute, RideRouteForm} from "@models/travelAllowance/rideRoute";
import {Coordinator} from "@models/travelAllowance/ride";
import {Links} from "@app/links";
import {ReactComponent as SaveIcon} from '@assets/images/travelAllowance/save.svg';
import {RideRoutePaneType} from "@enums/travelAllowance/rideRoute";
import {RideMatrix} from "@models/travelAllowance/rideMatrix";
import {getRideMatrix} from "@services/travelAllowance/rideMatrix.service";
import StaticPane from "@components/pane/static-pane.component";
import SubRoutesFormField from "@app/travelAllowance/rideRoute/components/subRoutesFormField.component";
import TravelAllowanceFormSkeleton from "@app/travelAllowance/common/components/travelAllowanceFormSkeleton.component";
import {useAutoScrollInputs} from "@hooks/useInputAutoScroll";
import {getDistance} from "@services/google/googleMaps.service";
import {transformSubRoutesToFormField, updateSubRouteHiddenFields, validateSubRoutes} from "@services/travelAllowance/subRoute.service";
import {Coordinates} from "@models/google/googleMaps";

type RideRouteAddEditPaneProps = {
    type: RideRoutePaneType,
    topEdge?: number,
    rideRouteDefaultValues?: RideRoute
}

const RideRouteAddEditPane: React.FC<RideRouteAddEditPaneProps> = ({type, topEdge, rideRouteDefaultValues}: RideRouteAddEditPaneProps) => {
    const cancelToken = axios.CancelToken.source();

    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const form = useForm<RideRouteForm>(
        {
            mode: 'onChange',
            defaultValues: {
                startingPlace: rideRouteDefaultValues?.startingPlace,
                destinationPlace: rideRouteDefaultValues?.destinationPlace,
                startingPlaceCountry: rideRouteDefaultValues?.startingPlaceCountry,
                destinationPlaceCountry: rideRouteDefaultValues?.destinationPlaceCountry,
                startingLatitude: rideRouteDefaultValues?.startingLatitude,
                destinationLatitude: rideRouteDefaultValues?.destinationLatitude,
                startingLongitude: rideRouteDefaultValues?.startingLongitude,
                destinationLongitude: rideRouteDefaultValues?.destinationLongitude,
                distance: rideRouteDefaultValues?.distance,
                isLocal: rideRouteDefaultValues?.isLocal,
                coordinator: rideRouteDefaultValues?.coordinator.id,
                placeOfService: rideRouteDefaultValues?.placeOfService.id,
                subRoutes: rideRouteDefaultValues?.subRoutes ? transformSubRoutesToFormField(rideRouteDefaultValues.subRoutes) : []
            }
        }
    );
    const {register, handleSubmit, control, setValue, errors, watch, reset, getValues} = form;

    const startingPlaceCoordinatesDefaultValues =
        rideRouteDefaultValues?.startingLatitude && rideRouteDefaultValues.startingLongitude
            ? {latitude: rideRouteDefaultValues?.startingLatitude, longitude: rideRouteDefaultValues?.startingLongitude}
            : undefined;

    const destinationPlaceCoordinatesDefaultValues =
        rideRouteDefaultValues?.destinationLatitude && rideRouteDefaultValues.destinationLongitude
            ? {latitude: rideRouteDefaultValues?.destinationLatitude, longitude: rideRouteDefaultValues?.destinationLongitude}
            : undefined;

    const [coordinator, setCoordinator] = useState<Coordinator | undefined>(rideRouteDefaultValues?.coordinator);
    const [placeOfService, setPlaceOfService] = useState<PlaceOfService | undefined>(rideRouteDefaultValues?.placeOfService);
    const [startingPlaceCoordinates, setStartingPlaceCoordinates] = useState<Coordinates | undefined>(startingPlaceCoordinatesDefaultValues);
    const [destinationPlaceCoordinates, setDestinationPlaceCoordinates] = useState<Coordinates | undefined>(destinationPlaceCoordinatesDefaultValues);

    const [rideMatrix, setRideMatrix] = useState<RideMatrix>();
    const [isCoordinatorModalOpen, setIsCoordinatorModalOpen] = useState<boolean>(false);
    const [isPlaceOfServiceModalOpen, setIsPlaceOfServiceModalOpen] = useState<boolean>(false);

    const [showToast, setShowToast] = useState<boolean>(false);
    const [toast, setToast] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showDistanceErrorAlert, setShowDistanceErrorAlert] = useState<boolean>(false);

    const startingPlace = watch('startingPlace');
    const destinationPlace = watch('destinationPlace');

    useAutoScrollInputs(loading);

    useEffect(() => {
        const shouldFetch = location.pathname.includes(Links.travelAllowance.rideRoute.add) || location.pathname.includes(Links.travelAllowance.rideRoute.edit);
        if (!shouldFetch) return;

        setLoading(true);

        getRideMatrix()
            .then((rideMatrix: RideMatrix) => setRideMatrix(rideMatrix))
            .finally(() => {
                setLoading(false);
            });
    }, [location.pathname]);

    useEffect(() => {
        if (!startingPlace || !destinationPlace) {
            setValue('distance', undefined);
        }
    }, [startingPlace, destinationPlace]);

    const fetchDistance = async () => {
        const route = control.getValues();

        if (!route.startingPlace || !route.destinationPlace) return;

        if (route.subRoutes) {
            if (!validateSubRoutes(route.subRoutes)) return;
        }

        const handleError = (error: any, errorMessageKey: string) => {
            const errorMessage = error.response.data.detail;

            if (errorMessage === 'Could not determine distance.') {
                setShowDistanceErrorAlert(true);
            } else {
                setToast(t(errorMessageKey));
                setShowToast(true);
            }

            console.error(error);
        };

        setShowLoader(true);

        let waypoints: string[] | undefined;
        if (route.subRoutes && route.subRoutes.length > 1) {
            waypoints = route.subRoutes?.slice(1).map(subRoute => subRoute.startingPlace);
        }

        getDistance(route.startingPlace, route.destinationPlace, waypoints)
            .then((data) => {
                setValue('distance', data.distance.total, {shouldValidate: true});

                if (waypoints) {
                    data.distance.waypoints.forEach((waypoint, index) => {
                        setValue(`subRoutes.${index}.distance`, waypoint.distance);
                    });
                }
            })
            .catch((error) => {
                handleError(error, "travelAllowance.rideRoute.distanceCalculateError")
            })
            .finally(() => {
                setShowLoader(false);
            })
    }

    const setStartingPlaceCountryCallback = (country: string) => {
        setValue('startingPlaceCountry', country);
    }

    const setDestinationPlaceCountryCallback = (country: string) => {
        setValue('destinationPlaceCountry', country);
    }

    const handleCoordinatorSelect = (coordinator: Coordinator) => {
        setCoordinator(coordinator)
        setValue('coordinator', coordinator.id, {shouldValidate: true});

        setPlaceOfService(undefined);
        setValue('placeOfService', undefined, {shouldValidate: true});
    }

    const handlePlaceOfServiceSelect = (placeOfService: PlaceOfService) => {
        setPlaceOfService(placeOfService);
        setValue('placeOfService', placeOfService.id, {shouldValidate: true});
    }

    const handleStartingCoordinatesSet = (coordinates?: Coordinates) => {
        setValue('startingLatitude', coordinates?.latitude);
        setValue('startingLongitude', coordinates?.longitude);

        setStartingPlaceCoordinates(coordinates);
    }

    const handleDestinationCoordinatesSet = (coordinates?: Coordinates) => {
        setValue('destinationLatitude', coordinates?.latitude);
        setValue('destinationLongitude', coordinates?.longitude);

        setDestinationPlaceCoordinates(coordinates);
    }

    const handleDistanceErrorAlertConfirm = () => {
        reset({
            startingPlace: '',
            destinationPlace: '',
            startingPlaceCountry: '',
            destinationPlaceCountry: '',
            distance: undefined,
            isLocal: undefined,
            startingLatitude: undefined,
            startingLongitude: undefined,
            destinationLatitude: undefined,
            destinationLongitude: undefined,
        });
    }

    const onSubmit = async (data: RideRouteForm) => {
        setShowLoader(true);

        const handleSuccess = () => {
            history.replace(Links.main + Links.travelAllowance.rideRoute.list);

            setShowLoader(false);
        };

        const handleError = (error: any, errorMessageKey?: string) => {
            if (errorMessageKey) {
                setToast(t(errorMessageKey));
            } else {
                const errorMessage = error.response.data.detail;

                if (errorMessage.includes('Data integrity failed for coordinatorId')) {
                    setToast(t("travelAllowance.rideRoute.coordinatorIntegrityViolation"));
                }

                if (errorMessage.includes('Data integrity failed for placeOfServiceId')) {
                    setToast(t("travelAllowance.rideRoute.placeOfServiceIntegrityViolation"));
                }
            }

            setShowToast(true);
            setShowLoader(false);

            console.error(error);
        };

        if (type === RideRoutePaneType.ADD) {
            addRideRoute(data)
                .then(handleSuccess)
                .catch((error) => handleError(error, "travelAllowance.rideRoute.addError"));
        }

        if (rideRouteDefaultValues?.id && type === RideRoutePaneType.EDIT) {
            let validationSuccess = true;

            await validateRideRoute(rideRouteDefaultValues.id)
                .catch((error) => {
                    handleError(error);
                    validationSuccess = false;
                });

            if (!validationSuccess) {
                return;
            }

            editRideRoute(rideRouteDefaultValues.id, data)
                .then(handleSuccess)
                .catch((error) => handleError(error, "travelAllowance.rideRoute.editError"));
        }
    }

    const updateHiddenFields = () => {
        const {subRoutes} = control.getValues();

        if (subRoutes) {
            updateSubRouteHiddenFields(subRoutes, setValue, getValues);
        }
    }

    return (
        <StaticPane topEdge={topEdge} marginTop={40} paddingTop={30} paddingBottom={147} hideGrabber={true}>
            {
                <StyledTravelAllowanceContent>
                    <IonLoading onDidDismiss={() => setShowLoader(false)} isOpen={showLoader}/>
                    <IonAlert
                        isOpen={showDistanceErrorAlert}
                        onDidDismiss={() => setShowDistanceErrorAlert(false)}
                        header={t('travelAllowance.rideRoute.distanceErrorAlert')}
                        buttons={[
                            {
                                text: t('common.ok'),
                                handler: () => {
                                    handleDistanceErrorAlertConfirm();
                                }
                            }
                        ]}
                    />
                    <IonToast
                        isOpen={showToast}
                        onDidDismiss={() => setShowToast(false)}
                        message={toast}
                        duration={6000}
                        position="top"
                        color="danger"
                    />
                    {loading
                        ? (
                            <TravelAllowanceFormSkeleton/>
                        ) : (
                            <>
                                <PaginatedListModal isOpen={isCoordinatorModalOpen}
                                                    fetchData={getPossibleCoordinators}
                                                    fetchParams={{date: undefined, cancelToken: cancelToken}}
                                                    setValue={handleCoordinatorSelect}
                                                    onClose={() => setIsCoordinatorModalOpen(false)}
                                />
                                <PaginatedListModal isOpen={isPlaceOfServiceModalOpen}
                                                    fetchData={getPlacesOfService}
                                                    fetchParams={{
                                                        coordinatorId: coordinator?.id,
                                                        startingPlaceCoordinates: startingPlaceCoordinates,
                                                        destinationPlaceCoordinates: destinationPlaceCoordinates,
                                                        localizationDistanceLimit: rideMatrix?.localizationDistanceLimit,
                                                        cancelToken: cancelToken
                                                    }}
                                                    fallbackFetchParams={
                                                        startingPlaceCoordinates && destinationPlaceCoordinates && rideMatrix?.localizationDistanceLimit
                                                            ? {
                                                                coordinatorId: coordinator?.id,
                                                                startingPlaceCoordinates: undefined,
                                                                destinationPlaceCoordinates: undefined,
                                                                cancelToken: undefined
                                                            }
                                                            : {
                                                                coordinatorId: coordinator?.id,
                                                            }
                                                    }
                                                    fallbackFetchButtonTitle={t('travel_allowance.rideRoute.placeOfService.loadRest')}
                                                    setValue={handlePlaceOfServiceSelect}
                                                    onClose={() => setIsPlaceOfServiceModalOpen(false)}
                                />
                                <FormProvider {...form}>
                                    <Form.Container onSubmit={handleSubmit(onSubmit)}>
                                        <Accordion isOpen={true} allowToggle={true} title={t('travelAllowance.generalData')}>
                                            {
                                                <section>
                                                    <IonRow className="mt-8">
                                                        <IonCol size="12">
                                                            <IonLabel>
                                                                {t('travelAllowance.rideRoute.startingPlace')}
                                                            </IonLabel>
                                                        </IonCol>
                                                    </IonRow>
                                                    <IonRow>
                                                        <IonCol size="12">
                                                            <Controller
                                                                name={'startingPlace'}
                                                                control={control}
                                                                defaultValue={''}
                                                                rules={{required: true}}
                                                                render={({onChange}) => (
                                                                    <CityAutocomplete setCountry={setStartingPlaceCountryCallback}
                                                                                      setCoordinates={handleStartingCoordinatesSet}
                                                                                      onChangeCallback={async (place: any) => {
                                                                                          onChange(place);
                                                                                          updateHiddenFields();
                                                                                          await fetchDistance();
                                                                                      }}
                                                                                      defaultValue={startingPlace}
                                                                                      className={errors.startingPlace ? 'error' : ''}/>
                                                                )}
                                                            />
                                                            <input ref={register}
                                                                   name={'startingPlaceCountry'}
                                                                   type={"hidden"}
                                                            />
                                                            <input ref={register}
                                                                   name={'startingLatitude'}
                                                                   type={"hidden"}
                                                            />
                                                            <input ref={register}
                                                                   name={'startingLongitude'}
                                                                   type={"hidden"}
                                                            />
                                                        </IonCol>
                                                    </IonRow>
                                                    <SubRoutesFormField
                                                        onRowAdd={async () => {
                                                            updateHiddenFields();
                                                        }}
                                                        onRowRemove={async () => {
                                                            updateHiddenFields();
                                                            await fetchDistance();
                                                        }}
                                                        onRowChange={async () => {
                                                            updateHiddenFields();
                                                            await fetchDistance();
                                                        }}
                                                    />
                                                    <IonRow className="mt-8">
                                                        <IonCol size="12">
                                                            <IonLabel>
                                                                {t('travelAllowance.rideRoute.destinationPlace')}
                                                            </IonLabel>
                                                        </IonCol>
                                                    </IonRow>
                                                    <IonRow>
                                                        <IonCol size="12">
                                                            <Controller
                                                                name={'destinationPlace'}
                                                                control={control}
                                                                defaultValue={''}
                                                                rules={{required: true}}
                                                                render={({onChange}) => (
                                                                    <CityAutocomplete setCountry={setDestinationPlaceCountryCallback}
                                                                                      setCoordinates={handleDestinationCoordinatesSet}
                                                                                      onChangeCallback={async (place: any) => {
                                                                                          onChange(place);
                                                                                          updateHiddenFields();
                                                                                          await fetchDistance();
                                                                                      }}
                                                                                      defaultValue={destinationPlace}
                                                                                      className={errors.destinationPlace ? 'error' : ''}/>
                                                                )}
                                                            />
                                                            <input ref={register}
                                                                   name={'destinationPlaceCountry'}
                                                                   type={"hidden"}
                                                            />
                                                            <input ref={register}
                                                                   name={'destinationLatitude'}
                                                                   type={"hidden"}
                                                            />
                                                            <input ref={register}
                                                                   name={'destinationLongitude'}
                                                                   type={"hidden"}
                                                            />
                                                        </IonCol>
                                                    </IonRow>
                                                    <input ref={register({setValueAs: v => v === 'true'})}
                                                           name={'isLocal'}
                                                           type={"hidden"}/>
                                                    <IonRow className="mt-8">
                                                        <IonCol size="12">
                                                            <IonLabel>
                                                                {t('travelAllowance.rideRoute.distance')}
                                                            </IonLabel>
                                                        </IonCol>
                                                    </IonRow>
                                                    <StyledInputGroup unit={true} className={errors.distance ? 'error' : ''}>
                                                        <IonCol size="10" className="label">
                                                            <StyledInput name="distance"
                                                                         type="number"
                                                                         ref={register({
                                                                             required: true,
                                                                             valueAsNumber: true,
                                                                         })}
                                                                         readOnly={rideMatrix?.isDistanceEditBlocked}
                                                            />
                                                        </IonCol>
                                                        <IonCol size="2" className="label">
                                                            <StyledInputUnit>km</StyledInputUnit>
                                                        </IonCol>
                                                    </StyledInputGroup>
                                                    <IonRow className="mt-8">
                                                        <IonCol size="12">
                                                            <IonLabel>
                                                                {t('travelAllowance.rideRoute.coordinator')}
                                                            </IonLabel>
                                                        </IonCol>
                                                    </IonRow>
                                                    <IonRow>
                                                        <IonCol size="12">
                                                            <Controller
                                                                name={'coordinator'}
                                                                control={control}
                                                                defaultValue={''}
                                                                rules={{
                                                                    required: true,
                                                                    valueAsNumber: true,
                                                                }}
                                                                render={() => (
                                                                    <StyledInput
                                                                        disabled={!startingPlace || !destinationPlace}
                                                                        placeholder={coordinator ? coordinator.name : t('travelAllowance.rideRoute.selectCoordinator')}
                                                                        className={errors.coordinator ? 'error' : ''}
                                                                        onClick={() => setIsCoordinatorModalOpen(prevState => !prevState)}
                                                                    >
                                                                    </StyledInput>
                                                                )}
                                                            />
                                                        </IonCol>
                                                    </IonRow>
                                                    <IonRow className="mt-8">
                                                        <IonCol size="12">
                                                            <IonLabel>
                                                                {t('travelAllowance.rideRoute.placeOfService')}
                                                            </IonLabel>
                                                        </IonCol>
                                                    </IonRow>
                                                    <IonRow>
                                                        <IonCol size="12">
                                                            <Controller
                                                                name={'placeOfService'}
                                                                control={control}
                                                                defaultValue={''}
                                                                rules={{
                                                                    required: true,
                                                                    valueAsNumber: true,
                                                                }}
                                                                render={() => (
                                                                    <StyledInput
                                                                        disabled={!coordinator}
                                                                        placeholder={placeOfService ? placeOfService.name : t('travelAllowance.rideRoute.selectPlaceOfService')}
                                                                        className={errors.placeOfService ? 'error' : ''}
                                                                        onClick={() => setIsPlaceOfServiceModalOpen(prevState => !prevState)}
                                                                    >
                                                                    </StyledInput>
                                                                )}
                                                            />
                                                        </IonCol>
                                                    </IonRow>
                                                </section>
                                            }
                                        </Accordion>
                                        <StyledButtonWrapper>
                                            <StyledButton type="submit">
                                                <div className="btn center">
                                                    <SaveIcon/>
                                                    <span>{t('travelAllowance.save')}</span>
                                                </div>
                                            </StyledButton>
                                        </StyledButtonWrapper>
                                    </Form.Container>
                                </FormProvider>
                            </>
                        )
                    }
                </StyledTravelAllowanceContent>
            }
        </StaticPane>
    );
};

export default RideRouteAddEditPane;